/**
 * PreOpAssessmentForm.js
 *
 * Unified pre-operative assessment screen. Sections:
 *   1. Patient History — allergies, medications, comorbidities
 *   2. Physical Examination — weight, height, airway (Mallampati), dental, vitals
 *   3. Classification — ASA class, fitness for surgery
 *   4. Safety Checklist — pathway-aware pre-op checklist items
 *   5. Notes
 */
import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Snackbar from '@material-ui/core/Snackbar';
import Alert from '@material-ui/lab/Alert';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Label from '../../Utils/FormInputs/Label';
import TextField from '../../Utils/FormInputs/TextField';
import SelectInput from '../../Utils/FormInputs/SelectInput';
import { ResizableTextarea } from '../../Utils/FormInputs/Textarea';
import { ChecklistItem } from '../shared';
import { PATHWAY, OPERATION_CLASS } from '../shared/constants';
import { surgeryService } from '../../../services/surgeryService';

const useStyles = makeStyles(() => ({
  root: {
    padding: '24px 28px',
    borderRadius: 8,
  },
  title: {
    fontSize: '1.1rem',
    fontWeight: 600,
    color: '#212121',
    marginBottom: 2,
  },
  subtitle: {
    fontSize: '0.82rem',
    color: '#757575',
    marginBottom: 20,
  },
  section: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: '0.7rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: '#9E9E9E',
    marginBottom: 14,
    paddingBottom: 8,
    borderBottom: '1px solid #F5F5F5',
  },
  // Checklist progress
  progressStrip: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 14,
  },
  progressBar: {
    flex: 1,
    height: 6,
    borderRadius: 3,
    backgroundColor: '#F5F5F5',
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: 3,
    transition: 'width 0.3s ease',
  },
  progressText: {
    fontSize: '0.78rem',
    fontWeight: 500,
    color: '#757575',
    whiteSpace: 'nowrap',
  },
  checklistContainer: {
    marginBottom: 4,
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 10,
    paddingTop: 16,
    borderTop: '1px solid #F5F5F5',
  },
}));

const ASA_OPTIONS = [
  { value: '', text: 'Select ASA Class' },
  { value: '1', text: 'ASA I  -  Healthy patient' },
  { value: '2', text: 'ASA II  -  Mild systemic disease' },
  { value: '3', text: 'ASA III  -  Severe systemic disease' },
  { value: '4', text: 'ASA IV  -  Incapacitating systemic disease' },
  { value: '5', text: 'ASA V  -  Moribund patient' },
  { value: '6', text: 'ASA VI  -  Brain-dead organ donor' },
];

const FITNESS_OPTIONS = [
  { value: '', text: 'Select Fitness Status' },
  { value: 'fit', text: 'Fit for Surgery' },
  { value: 'unfit', text: 'Unfit for Surgery' },
  { value: 'conditional', text: 'Conditional - Optimise First' },
];

const MALLAMPATI_OPTIONS = [
  { value: '', text: 'Select Mallampati Class' },
  { value: '1', text: 'Class I  -  Soft palate, fauces, uvula, pillars visible' },
  { value: '2', text: 'Class II  -  Soft palate, fauces, uvula visible' },
  { value: '3', text: 'Class III  -  Soft palate, base of uvula visible' },
  { value: '4', text: 'Class IV  -  Hard palate only visible' },
];

const DENTAL_OPTIONS = [
  { value: '', text: 'Select Dental Status' },
  { value: 'normal', text: 'Normal' },
  { value: 'dentures', text: 'Dentures' },
  { value: 'loose_teeth', text: 'Loose Teeth' },
  { value: 'caps_crowns', text: 'Caps / Crowns' },
  { value: 'poor', text: 'Poor Dentition' },
];

// ─── Checklist items by pathway ──────────────────────────────────────────────
const MAJOR_ITEMS = [
  { key: 'identity_verified', label: 'Patient identity verified (name, DOB, hospital number)' },
  { key: 'site_marked', label: 'Surgical site marked' },
  { key: 'consent_signed', label: 'Consent forms signed' },
  { key: 'allergies_reviewed', label: 'Allergies reviewed and documented' },
  { key: 'anaesthesia_assessment', label: 'Anaesthesia assessment complete' },
  { key: 'blood_crossmatch', label: 'Blood cross-match available' },
  { key: 'npo_confirmed', label: 'NPO status confirmed' },
  { key: 'antibiotics_ordered', label: 'Antibiotic prophylaxis ordered' },
  { key: 'dvt_prophylaxis', label: 'DVT prophylaxis ordered' },
  { key: 'blood_glucose', label: 'Blood glucose checked (if diabetic)' },
];

const MINOR_OUTPATIENT_ITEMS = [
  { key: 'identity_verified', label: 'Patient identity verified (name, DOB, hospital number)' },
  { key: 'consent_signed', label: 'Consent form signed' },
  { key: 'allergies_reviewed', label: 'Allergies reviewed' },
  { key: 'npo_confirmed', label: 'NPO status confirmed (if sedation)' },
  { key: 'escort_confirmed', label: 'Escort / companion confirmed' },
];

const EMPTY_FORM = {
  allergies: '',
  currentMedications: '',
  medicalHistory: '',
  weight: '',
  height: '',
  mallampati: '',
  dentalStatus: '',
  systolic: '',
  diastolic: '',
  heartRate: '',
  spo2: '',
  temperature: '',
  respiratoryRate: '',
  asaClass: '',
  fitnessStatus: '',
  notes: '',
};

const PreOpAssessmentForm = ({ surgeryId, pathway, operationClass, onSave, readOnly }) => {
  const classes = useStyles();

  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [checkedItems, setCheckedItems] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const isMinorOutpatient = pathway === PATHWAY.OUTPATIENT && operationClass === OPERATION_CLASS.MINOR;
  const checklistItems = isMinorOutpatient ? MINOR_OUTPATIENT_ITEMS : MAJOR_ITEMS;

  // Load existing assessment + checklist
  useEffect(() => {
    if (!surgeryId) return;
    surgeryService.viewPreOpAssessment(surgeryId).then((res) => {
      const raw = res?.data?.preoperative_notes;
      if (!raw) return;
      try {
        const saved = typeof raw === 'string' ? JSON.parse(raw) : raw;
        setForm((prev) => ({
          ...prev,
          allergies: saved.allergies || '',
          currentMedications: saved.current_medications || '',
          medicalHistory: saved.medical_history || '',
          weight: saved.weight || '',
          height: saved.height || '',
          mallampati: saved.mallampati || '',
          dentalStatus: saved.dental_status || '',
          systolic: saved.systolic || '',
          diastolic: saved.diastolic || '',
          heartRate: saved.heart_rate || '',
          spo2: saved.spo2 || '',
          temperature: saved.temperature || '',
          respiratoryRate: saved.respiratory_rate || '',
          asaClass: saved.asa_class || '',
          fitnessStatus: saved.fitness_status || '',
          notes: saved.notes || '',
        }));
        if (saved.checklist_items) setCheckedItems(saved.checklist_items);
      } catch {
        if (typeof raw === 'string') setForm((prev) => ({ ...prev, notes: raw }));
      }
    }).catch(() => {});
  }, [surgeryId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleToggle = (key) => () => {
    if (readOnly) return;
    setCheckedItems((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const checkedCount = checklistItems.filter((item) => checkedItems[item.key]).length;
  const allChecked = checkedCount === checklistItems.length;
  const pct = checklistItems.length > 0 ? Math.round((checkedCount / checklistItems.length) * 100) : 0;

  const getBarColor = () => {
    if (pct === 100) return '#4CAF50';
    if (pct >= 50) return '#FF9800';
    return '#EF5350';
  };

  const handleSave = async () => {
    setSubmitted(true);
    if (!form.asaClass || !form.fitnessStatus) return;

    setSaving(true);
    try {
      const payload = {
        surgery_id: surgeryId,
        allergies: form.allergies,
        current_medications: form.currentMedications,
        medical_history: form.medicalHistory,
        weight: form.weight,
        height: form.height,
        mallampati: form.mallampati,
        dental_status: form.dentalStatus,
        systolic: form.systolic,
        diastolic: form.diastolic,
        heart_rate: form.heartRate,
        spo2: form.spo2,
        temperature: form.temperature,
        respiratory_rate: form.respiratoryRate,
        asa_class: form.asaClass,
        fitness_status: form.fitnessStatus,
        notes: form.notes,
        // Checklist data — saved together
        checklist_items: checkedItems,
        checklist_completed: allChecked,
        checklist_pathway: pathway,
        checklist_operation_class: operationClass,
      };
      await surgeryService.createPreOpAssessment(payload);
      setSnackbar({ open: true, message: 'Assessment saved', severity: 'success' });
      if (onSave) onSave(payload);
    } catch {
      setSnackbar({ open: true, message: 'Failed to save assessment', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper className={classes.root} elevation={1}>
      <Typography className={classes.title}>Pre-Operative Assessment</Typography>
      <Typography className={classes.subtitle}>Anaesthetist clinical assessment and safety checklist</Typography>

      {/* ── Patient History ─────────────────────────────── */}
      <div className={classes.section}>
        <Typography className={classes.sectionTitle}>Patient History</Typography>
        <Row>
          <Col xs={12} md={4}>
            <Form.Group>
              <Label name="Known Allergies" />
              <ResizableTextarea
                name="allergies"
                rows="2"
                value={form.allergies}
                onChange={handleChange}
                disabled={readOnly}
                placeholder="e.g. Penicillin, Latex, NKDA..."
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={4}>
            <Form.Group>
              <Label name="Current Medications" />
              <ResizableTextarea
                name="currentMedications"
                rows="2"
                value={form.currentMedications}
                onChange={handleChange}
                disabled={readOnly}
                placeholder="e.g. Metformin 500mg BD, Lisinopril 10mg OD..."
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={4}>
            <Form.Group>
              <Label name="Relevant Medical History" />
              <ResizableTextarea
                name="medicalHistory"
                rows="2"
                value={form.medicalHistory}
                onChange={handleChange}
                disabled={readOnly}
                placeholder="e.g. Hypertension, Type 2 DM, Previous anaesthesia..."
              />
            </Form.Group>
          </Col>
        </Row>
      </div>

      {/* ── Physical Examination ────────────────────────── */}
      <div className={classes.section}>
        <Typography className={classes.sectionTitle}>Physical Examination</Typography>
        <Row className="mb-3">
          <Col xs={6} md={2}>
            <Form.Group>
              <Label name="Weight (kg)" />
              <TextField name="weight" type="number" value={form.weight} onChange={handleChange} placeholder="kg" disabled={readOnly} />
            </Form.Group>
          </Col>
          <Col xs={6} md={2}>
            <Form.Group>
              <Label name="Height (cm)" />
              <TextField name="height" type="number" value={form.height} onChange={handleChange} placeholder="cm" disabled={readOnly} />
            </Form.Group>
          </Col>
          <Col xs={12} md={4}>
            <Form.Group>
              <Label name="Airway — Mallampati" />
              <SelectInput name="mallampati" value={form.mallampati} onChange={handleChange} options={MALLAMPATI_OPTIONS} disabled={readOnly} />
            </Form.Group>
          </Col>
          <Col xs={12} md={4}>
            <Form.Group>
              <Label name="Dental Status" />
              <SelectInput name="dentalStatus" value={form.dentalStatus} onChange={handleChange} options={DENTAL_OPTIONS} disabled={readOnly} />
            </Form.Group>
          </Col>
        </Row>

        {/* Vital Signs */}
        <Row>
          <Col xs={6} sm={4} md={2}>
            <Form.Group>
              <Label name="Systolic" />
              <TextField name="systolic" type="number" value={form.systolic} onChange={handleChange} placeholder="mmHg" disabled={readOnly} />
            </Form.Group>
          </Col>
          <Col xs={6} sm={4} md={2}>
            <Form.Group>
              <Label name="Diastolic" />
              <TextField name="diastolic" type="number" value={form.diastolic} onChange={handleChange} placeholder="mmHg" disabled={readOnly} />
            </Form.Group>
          </Col>
          <Col xs={6} sm={4} md={2}>
            <Form.Group>
              <Label name="Heart Rate" />
              <TextField name="heartRate" type="number" value={form.heartRate} onChange={handleChange} placeholder="bpm" disabled={readOnly} />
            </Form.Group>
          </Col>
          <Col xs={6} sm={4} md={2}>
            <Form.Group>
              <Label name="SpO2" />
              <TextField name="spo2" type="number" value={form.spo2} onChange={handleChange} placeholder="%" disabled={readOnly} />
            </Form.Group>
          </Col>
          <Col xs={6} sm={4} md={2}>
            <Form.Group>
              <Label name="Temp" />
              <TextField name="temperature" type="number" value={form.temperature} onChange={handleChange} placeholder="°C" disabled={readOnly} />
            </Form.Group>
          </Col>
          <Col xs={6} sm={4} md={2}>
            <Form.Group>
              <Label name="Resp. Rate" />
              <TextField name="respiratoryRate" type="number" value={form.respiratoryRate} onChange={handleChange} placeholder="/min" disabled={readOnly} />
            </Form.Group>
          </Col>
        </Row>
      </div>

      {/* ── Classification ──────────────────────────────── */}
      <div className={classes.section}>
        <Typography className={classes.sectionTitle}>Classification</Typography>
        <Row>
          <Col xs={12} md={6}>
            <Form.Group>
              <Label name="ASA Classification" type />
              <SelectInput name="asaClass" value={form.asaClass} onChange={handleChange} options={ASA_OPTIONS} disabled={readOnly} />
              {submitted && !form.asaClass && (
                <span style={{ fontSize: '0.78rem', color: '#C62828' }}>ASA classification is required</span>
              )}
            </Form.Group>
          </Col>
          <Col xs={12} md={6}>
            <Form.Group>
              <Label name="Fitness for Surgery" type />
              <SelectInput name="fitnessStatus" value={form.fitnessStatus} onChange={handleChange} options={FITNESS_OPTIONS} disabled={readOnly} />
              {submitted && !form.fitnessStatus && (
                <span style={{ fontSize: '0.78rem', color: '#C62828' }}>Fitness status is required</span>
              )}
            </Form.Group>
          </Col>
        </Row>
      </div>

      {/* ── Safety Checklist ────────────────────────────── */}
      <div className={classes.section}>
        <Typography className={classes.sectionTitle}>
          Safety Checklist — {isMinorOutpatient ? 'Minor / Outpatient' : 'Major / Inpatient'}
        </Typography>

        <div className={classes.progressStrip}>
          <div className={classes.progressBar}>
            <div
              className={classes.progressFill}
              style={{ width: `${pct}%`, backgroundColor: getBarColor() }}
            />
          </div>
          <span className={classes.progressText}>
            {checkedCount} / {checklistItems.length}
          </span>
        </div>

        <div className={classes.checklistContainer}>
          {checklistItems.map((item) => (
            <ChecklistItem
              key={item.key}
              label={item.label}
              checked={!!checkedItems[item.key]}
              onChange={handleToggle(item.key)}
              required
              disabled={readOnly}
            />
          ))}
        </div>
      </div>

      {/* ── Notes ───────────────────────────────────────── */}
      <div className={classes.section}>
        <Typography className={classes.sectionTitle}>Additional Notes</Typography>
        <ResizableTextarea
          name="notes"
          rows="3"
          value={form.notes}
          onChange={handleChange}
          disabled={readOnly}
          placeholder="Any concerns, conditions to flag, or additional notes..."
        />
      </div>

      {/* ── Footer ──────────────────────────────────────── */}
      {!readOnly && (
        <div className={classes.footer}>
          <button
            type="button"
            className="btn btn-sm sms-info-btn px-4"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Assessment'}
          </button>
        </div>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          elevation={6}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default PreOpAssessmentForm;
